import { ArrowRight } from 'lucide-react';
import { Field, inputCls } from './fields';
import { useLang } from '@/context/LanguageContext';
import { inr, dueDate, TENURES, AMOUNT_MIN, AMOUNT_MAX, AMOUNT_STEP } from '@/lib/loan';

export default function ApplyStepLoan({ a, form, set, locale, errors, onNext }) {
  const { t } = useLang();
  const q = {
    interest: Math.round(form.amount * 0.001 * form.tenure_days),
    fee: Math.max(Math.round(form.amount * 0.025), 399),
  };
  const gst = Math.round(q.fee * 0.18);
  const pct = ((form.amount - AMOUNT_MIN) / (AMOUNT_MAX - AMOUNT_MIN)) * 100;

  return (
    <div className="space-y-8" data-testid="apply-step-1">
      <div>
        <div className="flex items-end justify-between">
          <label className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400">{a.amount}</label>
          <p className="font-mono text-3xl font-bold text-pulse" data-testid="step-1-amount-value">
            {inr(form.amount)}
          </p>
        </div>
        <input
          type="range"
          data-testid="step-1-amount-slider"
          min={AMOUNT_MIN}
          max={AMOUNT_MAX}
          step={AMOUNT_STEP}
          value={form.amount}
          onChange={(e) => set('amount', Number(e.target.value))}
          className="mt-4 w-full"
          style={{ '--fill': `${pct}%` }}
          aria-label="Loan amount"
        />
        <div className="mt-2 flex justify-between font-mono text-[11px] text-slate-600">
          <span>{inr(AMOUNT_MIN)}</span>
          <span>{inr(AMOUNT_MAX)}</span>
        </div>
      </div>

      <Field label={a.tenure} testid="step-1-tenure">
        <div className="flex flex-wrap gap-2">
          {TENURES.map((d) => (
            <button
              key={d}
              type="button"
              data-testid={`step-1-tenure-chip-${d}`}
              onClick={() => set('tenure_days', d)}
              className={`rounded-full px-5 py-2 font-mono text-sm font-semibold transition-all ${
                form.tenure_days === d
                  ? 'bg-pulse text-ink glow-pulse'
                  : 'bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white'
              }`}
            >
              {d} {t.rates.days}
            </button>
          ))}
        </div>
      </Field>

      <Field label={a.purpose} testid="step-1-purpose" error={errors.purpose}>
        <select
          data-testid="step-1-purpose-select"
          className={inputCls}
          value={form.purpose}
          onChange={(e) => set('purpose', e.target.value)}
        >
          {a.purposes.map((p) => (
            <option key={p} value={p} className="bg-ink">
              {p}
            </option>
          ))}
        </select>
      </Field>

      <div className="rounded-xl border border-pulse/25 bg-ink/60 p-5" data-testid="step-1-summary">
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-pulse">{a.summary}</p>
        <div className="mt-3 space-y-2 text-sm">
          <div className="flex justify-between" data-testid="step-1-summary-interest">
            <span className="text-slate-400">{a.interest}</span>
            <span className="font-mono text-white">{inr(q.interest)}</span>
          </div>
          <div className="flex justify-between" data-testid="step-1-summary-fee">
            <span className="text-slate-400">{a.fee}</span>
            <span className="font-mono text-white">{inr(q.fee + gst)}</span>
          </div>
          <div className="flex justify-between border-t border-white/10 pt-2.5" data-testid="step-1-summary-total">
            <span className="font-semibold text-white">{a.total}</span>
            <span className="font-mono text-lg font-bold text-pulse">
              {inr(form.amount + q.interest + q.fee + gst)}
            </span>
          </div>
          <div className="flex justify-between" data-testid="step-1-summary-due">
            <span className="text-slate-400">{a.due}</span>
            <span className="font-mono text-white">{dueDate(form.tenure_days, locale)}</span>
          </div>
        </div>
      </div>

      <button
        type="button"
        data-testid="apply-next-step-1"
        onClick={onNext}
        className="group flex w-full items-center justify-center gap-2 rounded-xl bg-pulse py-3.5 font-display font-bold text-ink transition-all hover:bg-pulse-dim"
      >
        {a.next} <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
      </button>
    </div>
  );
}
